<template>
    <div class="widget-component">
        <v-row align-content="end">
            <v-col cols="12" md="6">
                <h2 class="vd-inline-block color-primary">Availability</h2>
                <small
                    class="vd-inline-block font-weight-semi-bold ml-3"
                    v-if="!filteredProperties[0].singleProperty"
                >
                    Showing
                    {{
                        showAll || properties.length <= 6
                            ? properties.length
                            : 6
                    }}
                    of {{ properties.length }}
                </small>
            </v-col>

            <v-col
                cols="12"
                sm="9"
                v-if="!filteredProperties[0].singleProperty"
            >
                <label class="font-weight-bold">Bedrooms</label>

                <v-row
                    no-gutters
                    class="align-items-center justify-content-start"
                >
                    <v-checkbox
                        v-for="(bed, i) in availableBeds"
                        :key="i"
                        v-model="beds"
                        :label="bed.toString()"
                        :value="bed"
                        class="mr-5 w-auto"
                    ></v-checkbox>
                </v-row>
            </v-col>

            <v-col
                cols="12"
                sm="3"
                class="pt-0 pt-sm-12"
                v-if="!accordion && toggleAll"
            >
                <v-btn
                    color="primary"
                    small
                    block
                    elevation="0"
                    @click="expandAll()"
                >
                    {{ expanded.length > 0 ? "Close All" : "Open All" }}
                </v-btn>
            </v-col>

            <v-col cols="12">
                <div class="table-responsive">
                    <table class="table" cellspacing="0">
                        <thead>
                            <th
                                class="color-primary"
                                :class="
                                    filteredProperties[0].singleProperty
                                        ? 'vd-none'
                                        : ''
                                "
                            >
                                Plot No
                            </th>
                            <th class="color-primary">Type</th>
                            <th class="color-primary">
                                Bed<span class="vd-none vd-md-inline-block"
                                    >rooms</span
                                >
                            </th>
                            <th class="color-primary" v-if="showShareValues">
                                <v-menu
                                    open-on-hover
                                    bottom
                                    offset-y
                                    transition="scale-transition"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div v-bind="attrs" v-on="on">
                                            <span class="border-bottom-dashed">
                                                Full
                                                <span
                                                    class="vd-none vd-md-inline-block"
                                                    >Market Price</span
                                                >
                                                <span
                                                    class="vd-inline-block vd-md-none"
                                                    >Value</span
                                                >
                                            </span>
                                        </div>
                                    </template>

                                    <v-card elevation="2" max-width="260">
                                        <v-card-text>
                                            This is the total price of the
                                            property. When working out the share
                                            price, it'll be taken as a
                                            percentage of this value.
                                        </v-card-text>
                                    </v-card>
                                </v-menu>
                            </th>
                            <th class="color-primary" v-else-if="forSale">
                                Price
                            </th>
                            <th class="color-primary" v-else>Monthly rent</th>
                            <th
                                class="color-primary shares"
                                v-if="showShareValues"
                            >
                                <v-menu
                                    open-on-hover
                                    bottom
                                    offset-y
                                    transition="scale-transition"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div v-bind="attrs" v-on="on">
                                            <span
                                                class="border-bottom-dashed sharePercent"
                                            >
                                                Share %
                                            </span>
                                        </div>
                                    </template>

                                    <v-card elevation="2" max-width="260">
                                        <v-card-text class="shareExtraText">
                                            The smallest portion of this
                                            property you can buy. You pay rent
                                            on the remaining portion. It's
                                            sometimes possible to buy a larger
                                            share than this, depending on your
                                            circumstances and the conditions on
                                            the property.
                                        </v-card-text>
                                    </v-card>
                                </v-menu>
                            </th>
                            <th class="color-primary">Availability</th>
                            <!-- <th class="color-primary"><span class="vd-none vd-md-block">Downloads</span></th> -->
                        </thead>

                        <tbody
                            v-for="(property, index) in filteredProperties"
                            :key="index"
                        >
                            <tr
                                class="bg-gray-f1f3f5"
                                v-if="showAll || index < 6"
                                :class="
                                    expanded.includes(property.id)
                                        ? 'expanded'
                                        : ''
                                "
                            >
                                <td
                                    class="font-weight-bold"
                                    :class="
                                        property.singleProperty ? 'vd-none' : ''
                                    "
                                >
                                    {{ property.propertyNumber || "TBC" }}
                                </td>

                                <td>
                                    <div
                                        class="property-type justify-content-between"
                                    >
                                        <div>
                                            {{ property.propertyType }}
                                            <span
                                                v-if="
                                                    property.propertyType ==
                                                        'Flat' &&
                                                    has(property, 'floor.name')
                                                "
                                                >{{ property.floor.name }}</span
                                            >
                                        </div>

                                        <TypeIcons
                                            :property="property"
                                            :wheelchair-accessible="
                                                hasFeature(
                                                    property.id,
                                                    'Wheelchair accessible'
                                                )
                                            "
                                        >
                                        </TypeIcons>
                                    </div>
                                </td>

                                <td v-if="property.bedrooms > 0">
                                    {{ property.bedrooms }}
                                    <div class="vd-none vd-md-inline-block">
                                        bedroom{{
                                            property.bedrooms > 1 ? "s" : ""
                                        }}
                                    </div>
                                </td>
                                <td v-else>Studio</td>

                                <td v-if="forSale">
                                    {{ property.formattedFullMarketPrice }}
                                </td>

                                <td v-else>
                                    {{ "£" + property.monthlyRent }}
                                </td>

                                <td
                                    v-if="
                                        showShareValues &&
                                        property.availabilityStatus.code !=
                                            'coming-soon'
                                    "
                                >
                                    {{
                                        currencyFormatter.format(
                                            property.minShareValue
                                        )
                                    }}
                                    <span
                                        >{{
                                            property.minSharePercentage
                                        }}%</span
                                    >
                                </td>
                                <td v-else-if="showShareValues">TBC</td>

                                <td>
                                    {{ property.availabilityStatus.name }}
                                </td>

                                <td>
                                    <!-- <Downloads class="vd-none vd-md-inline-block" :property="property"></Downloads> -->

                                    <v-btn
                                        elevation="0"
                                        class="expand color-primary"
                                        @click="expand(property.id)"
                                    >
                                        <span
                                            class="more-details vd-none vd-md-inline-block"
                                            >{{
                                                normalizedOptions.details.button
                                                    .text
                                            }}</span
                                        >
                                        <v-icon
                                            class="color-primary"
                                            :class="
                                                expanded.includes(property.id)
                                                    ? 'active'
                                                    : ''
                                            "
                                        >
                                            mdi-chevron-down
                                        </v-icon>
                                    </v-btn>
                                </td>
                            </tr>

                            <tr
                                v-if="expanded.includes(property.id)"
                                class="bg-gray-f1f3f5"
                            >
                                <td colspan="12" class="property-dropdown">
                                    <div
                                        class="text-start mb-4 vd-block vd-sm-none"
                                    >
                                        <TypeIcons
                                            :property="property"
                                            :wheelchair-accessible="
                                                hasFeature(
                                                    property.id,
                                                    'Wheelchair accessible'
                                                )
                                            "
                                        >
                                        </TypeIcons>
                                    </div>

                                    <div class="property-info row mt-0">
                                        <div
                                            class="key-features col-12 col-md-6"
                                        >
                                            <div class="col-12 text-start">
                                                <h3 class="mb-0">
                                                    {{
                                                        normalizedOptions
                                                            .details.title
                                                    }}
                                                </h3>
                                            </div>
                                            <table>
                                                <tr
                                                    v-for="(
                                                        field, index
                                                    ) in normalizedOptions
                                                        .details.fields"
                                                    :class="
                                                        field.containerClasses
                                                    "
                                                    v-if="showDetailRow(field)"
                                                >
                                                    <td
                                                        :class="
                                                            field.title.classes
                                                        "
                                                    >
                                                        {{ field.title.text }}
                                                    </td>
                                                    <td
                                                        :class="
                                                            field.field.classes
                                                        "
                                                    >
                                                        {{
                                                            calculatedFieldValue(
                                                                field,
                                                                property
                                                            )
                                                        }}
                                                    </td>
                                                </tr>
                                            </table>
                                        </div>
                                        <div class="downloads col-12 col-md-6">
                                            <!-- Mobile Download buttons -->
                                            <Downloads
                                                id="mobile-downloads"
                                                class="vd-flex flex-column"
                                                :property="property"
                                                mobile
                                            ></Downloads>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </v-col>

            <v-col class="text-center">
                <v-btn
                    v-if="!showAll && properties.length > 6"
                    @click="showAll = true"
                    color="primary"
                    outlined
                    large
                    class="mb-6"
                >
                    Load more
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import {
    computed,
    defineComponent,
    onMounted,
    ref,
} from "@vue/composition-api";
import TypeIcons from "./micro/availability/TypeIcons.vue";
import Downloads from "./micro/availability/Downloads.vue";
import { get, has, merge } from "lodash";

export default defineComponent({
    props: {
        forSale: {
            default: true,
        },
        isSharedOwnership: {
            required: true,
        },
        schemeGroup: {
            required: true,
        },
        properties: {
            required: true,
        },
        bedrooms: {
            type: Array,
            required: true,
        },
        currencyFormatter: {
            required: true,
        },
        accordion: {
            required: false,
            default: true,
        },
        toggleAll: {
            required: false,
            default: false,
        },
        settings: {
            type: Object,
        },
        listing: {
            type: Object,
        },
    },
    components: { TypeIcons, Downloads },
    setup(props, context) {
        const availableBeds = ref([]);
        const beds = ref([]);
        const showAll = ref(false);
        const expanded = ref([]);

        const defaultSettings = ref({
            details: {
                button: {
                    text: "More details",
                },
                title: "Key Features",
                fields: [
                    {
                        title: {
                            classes: "",
                            text: "Share £",
                        },
                        field: {
                            classes: "",
                            value: "property.formattedMinShareValue",
                        },
                        containerClasses: "vd-md-none",
                        dependsOn: "showShareValues",
                    },
                    {
                        title: {
                            classes: "",
                            text: "Share %",
                        },
                        field: {
                            classes: "",
                            value: "property.formattedMinSharePercentage",
                        },
                        containerClasses: "vd-md-none",
                        dependsOn: "showShareValues",
                    },
                    {
                        title: {
                            classes: "",
                            text: "Availability",
                        },
                        field: {
                            classes: "",
                            value: "property.availabilityStatus.name",
                        },
                        containerClasses: "vd-md-none",
                        dependsOn: "showShareValues",
                    },
                    {
                        title: {
                            classes: "",
                            text: "Monthly rent",
                        },
                        field: {
                            classes: "",
                            value: "property.formattedMonthlyRent",
                        },
                        containerClasses: "",
                        dependsOn: "showMonthlyRent",
                    },
                    {
                        title: {
                            classes: "",
                            text: "Est. Monthly Service Charge",
                        },
                        field: {
                            classes: "",
                            value: "property.formattedMonthlyServiceCharge",
                        },
                        containerClasses: "",
                    },
                    {
                        title: {
                            classes: "",
                            text: "Est. Monthly Mortgage",
                        },
                        field: {
                            classes: "",
                            value: "property.formattedMonthlyMortgage",
                        },
                        containerClasses: "",
                        dependsOn: "showMonthlyMortgage",
                    },
                    {
                        title: {
                            classes: "",
                            text: "Est. Monthly Spend",
                        },
                        field: {
                            classes: "",
                            value: "property.formattedMonthlySpend",
                        },
                        containerClasses: "",
                        dependsOn: "showMonthlySpend",
                    },
                    {
                        title: {
                            classes: "",
                            text: "Council Tax Band",
                        },
                        field: {
                            classes: "",
                            value: "property.formattedCouncilTaxBand",
                        },
                        containerClasses: "",
                    },
                    {
                        title: {
                            classes: "",
                            text: "Tenure",
                        },
                        field: {
                            classes: "text-capitalize",
                            value: "property.tenure",
                        },
                        containerClasses: "",
                    },
                    /* other fields configurable via the JSON file:
                        {
                            title: {
                                classes: "",
                                text: "Floor area"
                            },
                            field: {
                                classes: "",
                                value: "property.formattedPropertyFloorArea"
                            },
                            containerClasses: ""
                        },
                        {
                            title: {
                                classes: "",
                                text: "Est. Monthly Mortgage"
                            },
                            field: {
                                classes: "",
                                value: "property.formattedPropertyMortgageInterestRate"
                            },
                            containerClasses: "",
                        },
                        {
                            title: {
                                classes: "",
                                text: "Suggested minimum income"
                            },
                            field: {
                                classes: "",
                                value: "property.formattedSuggestedMinIncome"
                            },
                            containerClasses: "",
                        },
                        */
                ],
            },
        });

        const normalizedOptions = computed(() => {
            let mergedSettings = merge(defaultSettings.value, props.settings);
            if (has(props, "settings.details.fields")) {
                if (props.settings.details.fields.length > 0) {
                    mergedSettings.details.fields =
                        props.settings.details.fields;
                }
            }
            return mergedSettings;
        });

        const filteredProperties = computed(() => {
            let results = [];

            props.properties.filter((obj) => {
                if (
                    beds.value.length == 0 ||
                    beds.value.indexOf(obj.bedrooms) != -1
                ) {
                    const formattedMonthlyRent =
                        obj.monthlyRent &&
                        obj.availabilityStatus.code != "coming-soon"
                            ? props.currencyFormatter.format(obj.monthlyRent)
                            : obj.subsidisedMonthlyRent &&
                              obj.availabilityStatus.code != "coming-soon"
                            ? props.currencyFormatter.format(
                                  obj.subsidisedMonthlyRent
                              )
                            : "TBC";

                    const formattedPropertyFloorArea = obj.floorArea
                        ? obj.floorArea +
                          " m² / " +
                          convertSquareMetersToSquareFeet(obj.floorArea) +
                          " ft²"
                        : "TBC";

                    const formattedPropertyMortgageInterestRate =
                        obj.mortgageInterestRate
                            ? obj.mortgageInterestRate + "%"
                            : props.listing.mortgageInterestRate
                            ? props.listing.mortgageInterestRate + "%"
                            : "TBC";

                    const formattedSuggestedMinIncome =
                        obj.suggestedMinIncome && obj.suggestedMinIncome > 0
                            ? props.currencyFormatter.format(
                                  obj.suggestedMinIncome
                              )
                            : "TBC";

                    const formattedFields = {
                        formattedFullMarketPrice:
                            obj.availabilityStatus.code != "coming-soon"
                                ? props.currencyFormatter.format(
                                      obj.fullMarketPrice
                                  )
                                : "TBC",
                        formattedMinShareValue:
                            obj.availabilityStatus.code != "coming-soon"
                                ? props.currencyFormatter.format(
                                      obj.minShareValue
                                  )
                                : "TBC",
                        formattedMinSharePercentage:
                            obj.availabilityStatus.code != "coming-soon"
                                ? `${obj.minSharePercentage}%`
                                : "TBC",
                        formattedMonthlyServiceCharge:
                            obj.monthlyServiceCharge &&
                            obj.availabilityStatus.code != "coming-soon"
                                ? props.currencyFormatter.format(
                                      obj.monthlyServiceCharge
                                  )
                                : "TBC",
                        formattedMonthlyMortgage:
                            obj.monthlyMortgage &&
                            obj.availabilityStatus.code != "coming-soon"
                                ? props.currencyFormatter.format(
                                      obj.monthlyMortgage
                                  )
                                : "TBC",
                        formattedMonthlySpend:
                            obj.monthlySpend &&
                            obj.availabilityStatus.code != "coming-soon"
                                ? props.currencyFormatter.format(
                                      obj.monthlySpend
                                  )
                                : "TBC",
                        formattedCouncilTaxBand:
                            obj.councilTaxBand &&
                            obj.availabilityStatus.code != "coming-soon"
                                ? obj.councilTaxBand
                                : "TBC",
                        formattedMonthlyRent: formattedMonthlyRent,
                        formattedPropertyFloorArea: formattedPropertyFloorArea,
                        formattedPropertyMortgageInterestRate:
                            formattedPropertyMortgageInterestRate,
                        formattedSuggestedMinIncome:
                            formattedSuggestedMinIncome,
                    };
                    results.push({ ...obj, ...formattedFields });
                }
            });
            return results;
        });

        const showShareValues = computed(() => {
            return props.forSale && props.isSharedOwnership;
        });

        const showMonthlyRent = computed(() => {
            if (props.isSharedOwnership) {
                return true;
            } else if (!props.forSale) {
                return true;
            }
            return false;
        });

        const showMonthlyMortgage = computed(() => {
            if (props.isSharedOwnership) {
                return true;
            }
            return false;
        });

        const showMonthlySpend = computed(() => {
            if (props.isSharedOwnership) {
                return true;
            }
            return false;
        });

        const computedProperties = {
            showShareValues: showShareValues,
            showMonthlyRent: showMonthlyRent,
            showMonthlyMortgage: showMonthlyMortgage,
            showMonthlySpend: showMonthlySpend,
            // Add more computed properties here
        };

        function hasFeature(id, feature) {
            const property = props.properties.filter((obj) => {
                return obj.id === id;
            });

            const check = property[0].propertyFeatures.filter((obj) => {
                return obj.values.includes(feature);
            });

            return check.length > 0;
        }

        function expand(id) {
            if (expanded.value.includes(id)) {
                for (let i = 0; i < expanded.value.length; i++) {
                    if (expanded.value[i] === id) {
                        expanded.value.splice(i, 1);
                    }
                }
            } else {
                if (props.accordion) {
                    expanded.value = [];
                }

                expanded.value.push(id);
            }
        }

        function expandAll() {
            if (expanded.value.length > 0) {
                expanded.value = [];
            } else {
                for (let i = 0; i < props.properties.length; i++) {
                    expanded.value.push(props.properties[i].id);
                }
            }
        }

        function calculatedFieldValue(field, propertyObj) {
            let path = field.field.value.split(".").slice(1);
            if (has(propertyObj, path)) {
                return get(propertyObj, path);
            }
        }

        function showDetailRow(field) {
            // Check if the field has the 'alwaysShow' property
            if (has(field, "alwaysShow")) {
                // If 'alwaysShow' is true, return true to show the detail row
                if (field.alwaysShow) {
                    return true;
                }
            }
            // If 'alwaysShow' is not present, check if the field has the 'dependsOn' property
            else if (has(field, "dependsOn")) {
                // Get the computed property name from 'dependsOn'
                let compProp = field.dependsOn;
                // Check if the computed property exists and has a 'value' property
                // Interestingly, I needed to create a fresh object that holds the computed properties, allowing us to invoke them using a variable.
                if (has(computedProperties[compProp], "value")) {
                    // Return the value of the computed property to determine whether to show the detail row
                    return computedProperties[compProp].value;
                }
            }
            // If neither 'alwaysShow' nor 'dependsOn' is present, default to true and show the detail row
            return true;
        }

        function convertSquareMetersToSquareFeet(squareMeters) {
            const squareFeet = squareMeters * 10.76391041671;
            return squareFeet.toFixed(1);
        }

        onMounted(() => {
            // Build our bed array for the checkbox filters
            props.properties.forEach((property) => {
                if (!availableBeds.value.includes(property.bedrooms)) {
                    availableBeds.value.push(property.bedrooms);
                }
            });

            availableBeds.value.sort();
        });

        return {
            availableBeds,
            beds,
            expanded,
            filteredProperties,
            showShareValues,
            hasFeature,
            expand,
            expandAll,
            showAll,
            has,
            normalizedOptions,
            calculatedFieldValue,
            showDetailRow,
        };
    },
});
</script>

<style lang="scss">
@import "~vuetify/src/styles/styles.sass";

.table-responsive {
    width: 100%;
    overflow: auto;

    > .table {
        text-align: left;
        min-width: 100%;

        span,
        div {
            padding: 0px !important;
        }

        thead {
            th {
                font-size: 15px;
                font-weight: 600;
                padding-bottom: 10px;

                &:first-child {
                    @media #{map-get($display-breakpoints, 'lg-and-up')} {
                        padding-left: 35px;
                    }
                }

                &:last-child {
                    max-width: 260px;

                    @media #{map-get($display-breakpoints, 'md-and-down')} {
                        max-width: 30px;
                    }
                }

                @media #{map-get($display-breakpoints, 'sm-and-down')} {
                    min-width: 40px;
                    font-size: 13px;
                    padding-left: 10px;
                    padding-right: 10px;

                    &:nth-child(5),
                    &:nth-child(6) {
                        display: none;
                    }
                }
            }
        }

        tbody {
            border: 0px;

            tr {
                td {
                    color: #545759;
                    font-weight: 600;
                    border: 0px;
                    vertical-align: top;
                    padding-top: 20px;
                    padding-bottom: 15px;
                    padding-left: 17px;
                    padding-right: 17px;
                    border-top: 6px solid #fff;
                    border-bottom: 6px solid #fff;
                    font-size: 15px;

                    &:first-child {
                        font-size: 22px;

                        &.property-dropdown {
                            padding-left: 35px;
                            padding-right: 35px;
                            padding-bottom: 35px;
                            padding-top: 41px;

                            background: linear-gradient(
                                180deg,
                                #bfc7d3 0%,
                                #dde2e6 3.13%
                            );
                            border-top: none;

                            @media #{map-get($display-breakpoints, 'sm-and-down')} {
                                padding-top: 19px;
                            }
                        }

                        @media #{map-get($display-breakpoints, 'sm-and-down')} {
                            font-size: 17px;
                        }
                    }

                    .property-type {
                        display: flex;
                        align-items: center;
                        justify-content: flex-start;

                        .v-icon {
                            font-size: 32px;

                            // &:first-of-type{
                            //     margin-left: 10px;
                            // }

                            @media #{map-get($display-breakpoints, 'sm-and-down')} {
                                display: none;
                            }
                        }
                    }

                    span:not(.v-btn__content) {
                        width: 100%;
                        display: block;
                        font-size: 14px;
                        color: #80807c;

                        @media #{map-get($display-breakpoints, 'sm-and-down')} {
                            display: none;
                        }
                    }

                    .v-btn {
                        &.expand {
                            background: #fff;
                            padding-left: 19.5px;
                            padding-right: 14.5px;
                            min-width: 36px;
                            border: 1px solid var(--v-primary-base);
                            border-radius: 4px;

                            span.more-details {
                                color: var(--v-primary-base);
                                font-size: 15px;
                                margin-right: 18px;
                            }

                            .v-icon {
                                transition: all 0.2s ease;
                            }

                            .active {
                                transform: rotate(180deg);
                            }

                            @media #{map-get($display-breakpoints, 'sm-and-down')} {
                                min-width: 28px;
                                width: 28px;
                                height: 28px;
                                border-width: 2px;
                                padding-left: 5px;
                                padding-right: 5px;
                            }
                        }
                    }

                    @media #{map-get($display-breakpoints, 'sm-and-down')} {
                        font-size: 13px;
                        padding-top: 10px;
                        padding-bottom: 10px;
                        padding-left: 10px;
                        padding-right: 10px;
                        vertical-align: middle;
                        border-top: 1px solid #d6d1c9;

                        &:last-child {
                            text-align: right;
                        }

                        &:nth-child(5),
                        &:nth-child(6) {
                            display: none;
                        }
                    }

                    @media #{map-get($display-breakpoints, 'xs-only')} {
                        padding-top: 5px;
                        padding-bottom: 5px;
                    }

                    .key-features {
                        h3 {
                            color: var(--v-primary-base);
                            font-size: 21px;
                        }
                    }

                    // Nested table inside expanded area (mobile)
                    table {
                        min-width: 160px;
                        border: 0px;
                        width: 100%;

                        tr {
                            background-color: transparent !important;
                            border-bottom: 1px solid #545759;

                            &:last-child {
                                border-bottom: none;
                            }

                            td,
                            td:first-child {
                                padding: 11px 0px !important;
                                font-size: 15px;
                                border: 0px !important;
                                text-align: left !important;
                                background-color: transparent;

                                &:first-child {
                                    width: 70%;
                                }

                                @media #{map-get($display-breakpoints, 'sm-and-down')} {
                                    font-size: 13px;
                                }
                            }
                        }
                    }
                }

                &:first-child {
                    @media #{map-get($display-breakpoints, 'sm-and-down')} {
                        background-color: #fff;

                        td {
                            border-bottom: none;

                            &:not(:last-child) {
                                border-right: 1px solid #d6d1c9;
                            }
                        }
                    }
                }

                &:last-child {
                    @media #{map-get($display-breakpoints, 'sm-and-down')} {
                        td {
                            border-bottom: 1px solid #d6d1c9;
                        }
                    }
                }

                &.expanded {
                    > td {
                        border-bottom: none;
                    }

                    @media #{map-get($display-breakpoints, 'sm-and-down')} {
                        background-color: #f1f3f5;
                    }
                }
            }
        }
    }

    #mobile-downloads {
        align-items: center;

        .v-btn {
            font-size: 21px;
            height: 52px;

            @media #{map-get($display-breakpoints, 'sm-and-up')} {
                min-width: 270px;
            }

            @media #{map-get($display-breakpoints, 'xs-only')} {
                width: 100%;
            }
        }

        @media #{map-get($display-breakpoints, 'sm-and-down')} {
            margin-top: 50px;
        }
    }
}
</style>
